import React, { useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import Select from "react-select";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Input } from "reactstrap";
import { RefreshCw, X } from "react-feather";
import { toast } from "react-toastify";
import GeneralLedgerService from "../../../Services/generalLedger/GeneralLedgerService";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import { formatAmount, formatMoney } from "../../../utils/formatMoney";
import { can, PERMISSIONS } from "./glUtils";
import { TABLE_STYLES, useStickyTableHeader } from "./tableLayout";
import { ConfidenceBadge, ReconStatusBadge, STATUS_CHIPS, incomeTypeLabel } from "./incomeReconMeta";
import IncomeReconReviewModal from "./IncomeReconReviewModal";

const SELECT_STYLES = {
  menuPortal: (base) => ({ ...base, zIndex: 1060 }),
  menu: (base) => ({ ...base, fontSize: "0.85rem", minWidth: 200 }),
};

const EMPTY_FILTERS = { provider: "", currency: "", from: "", to: "", status: "" };

const toOptions = (values) => (values || []).map((value) => ({ value: String(value), label: String(value) }));

function CurrencySummary({ summary }) {
  const currencies = summary?.currencies || [];
  if (currencies.length === 0) return null;
  return (
    <div className="gl-totals-strip mb-2">
      {currencies.map((total) => {
        const difference = Number(total.income_total) - Number(total.matched_total);
        return (
          <div key={total.currency} className="gl-total-item">
            <div className="gl-total-head">
              <span className="gl-total-currency">{total.currency || "—"}</span>
              <span className="gl-total-count">
                {total.confirmed_count} confirmed · {total.pending_count} pending · {total.unmatched_count} unmatched
              </span>
            </div>
            <div className="gl-total-values">
              <div>
                <small>Income</small>
                <strong>{formatAmount(total.income_total, total.currency)}</strong>
              </div>
              <div>
                <small>Matched</small>
                <strong className="text-success">{formatAmount(total.matched_total, total.currency)}</strong>
              </div>
              <div>
                <small>Difference</small>
                <strong className={difference > 0 ? "text-danger" : "text-success"}>{formatAmount(difference, total.currency)}</strong>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function IncomeReconTab({ meta }) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [reviewing, setReviewing] = useState(null);
  const [tableRef, stickyStyle] = useStickyTableHeader();
  const canMutate = can(meta, PERMISSIONS.ANNOTATE);

  const update = (patch) => setFilters((current) => ({ ...current, ...patch }));

  const params = {
    ...(filters.provider && { provider: filters.provider }),
    ...(filters.currency && { currency: filters.currency }),
    ...(filters.from && { from: filters.from }),
    ...(filters.to && { to: filters.to }),
    ...(filters.status && { status: filters.status }),
  };

  const query = useQuery({
    queryKey: ["glIncomeRecon", params],
    queryFn: () => GeneralLedgerService.getIncomeReconciliation(params),
    placeholderData: (previous) => previous,
  });

  const dashboard = query.data?.data;
  const summary = dashboard?.summary;
  const items = dashboard?.items || [];
  const rows = useMemo(
    () => items.map((item) => ({ ...item, key: `${item.income.income_type}:${item.income.income_id}` })),
    [items]
  );
  const hasFilters = Boolean(filters.provider || filters.currency || filters.from || filters.to || filters.status);

  const providerOptions = useMemo(() => toOptions(meta?.providers), [meta]);
  const currencyOptions = useMemo(() => toOptions(meta?.currencies), [meta]);

  const scan = useMutation({
    mutationFn: () => GeneralLedgerService.scanIncomeReconciliation(params),
    onSuccess: (response) => {
      const result = response?.data || {};
      toast.success(
        `Scan complete: ${result.scanned ?? 0} records examined, ${result.persisted ?? 0} saved, ${result.auto_confirmed ?? 0} auto-confirmed.`
      );
      queryClient.invalidateQueries({ queryKey: ["glIncomeRecon"] });
    },
    onError: (error) => toast.error(getApiErrorMessage(error, "The scan could not be run.")),
  });

  const columns = [
    {
      id: "income",
      name: "Income",
      grow: 3,
      minWidth: "220px",
      cell: (row) => (
        <div className="gl-desc">
          <div className="gl-cell-strong" title={row.income.title}>
            {row.income.title || `Income #${row.income.income_id}`}
          </div>
          {row.income.artist_full_name && row.income.artist_full_name !== row.income.title && (
            <div className="gl-muted small">as “{row.income.artist_full_name}”</div>
          )}
          {Number(row.income.artist_count) > 0 && (
            <div className="gl-muted small">{row.income.artist_count} artist{row.income.artist_count === 1 ? "" : "s"} pooled</div>
          )}
          <span className="gl-party-badges">
            <span className="gl-party-type gl-party-provider">{row.income.provider || "Unknown provider"}</span>
            <span className="gl-recon-type">{incomeTypeLabel(row.income.income_type, row.income.provider)}</span>
          </span>
        </div>
      ),
    },
    {
      id: "period",
      name: "Period",
      width: "110px",
      cell: (row) => <span className="gl-mono">{row.period || "—"}</span>,
    },
    {
      id: "currency",
      name: "Currency",
      width: "80px",
      center: true,
      cell: (row) =>
        row.income.currency ? (
          <span className="gl-currency-badge">{row.income.currency}</span>
        ) : (
          <span className="gl-muted">—</span>
        ),
    },
    {
      id: "amount",
      name: "Amount",
      right: true,
      width: "140px",
      cell: (row) => <span className="gl-amount">{formatMoney(row.income.amount, row.income.currency)}</span>,
    },
    {
      id: "matched",
      name: "Matched",
      right: true,
      width: "140px",
      cell: (row) =>
        row.status === "confirmed" && row.decision ? (
          <span className="gl-amount text-success">{formatMoney(row.decision.matched_amount, row.income.currency)}</span>
        ) : (
          <span className="gl-muted">—</span>
        ),
    },
    {
      id: "status",
      name: "Status",
      minWidth: "150px",
      cell: (row) => <ReconStatusBadge status={row.status} matchType={row.match_type} />,
    },
    {
      id: "confidence",
      name: "Match",
      width: "86px",
      right: true,
      cell: (row) => <ConfidenceBadge score={row.confidence} />,
    },
    {
      id: "actions",
      name: "",
      width: "100px",
      cell: (row) => (
        <Button size="sm" color="light" className="btn-xs" onClick={() => setReviewing(row)}>
          {canMutate && row.status !== "confirmed" ? "Review" : "View"}
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div className="gl-filter-bar">
        <div className="gl-date-range">
          <Input
            type="date"
            aria-label="From"
            title="Income period from"
            className={filters.from ? "" : "gl-date-empty"}
            value={filters.from}
            onChange={(e) => update({ from: e.target.value })}
          />
          <span className="gl-muted">→</span>
          <Input
            type="date"
            aria-label="To"
            title="Income period to"
            className={filters.to ? "" : "gl-date-empty"}
            value={filters.to}
            onChange={(e) => update({ to: e.target.value })}
          />
        </div>
        <div className="gl-filter-select">
          <Select
            classNamePrefix="gl-select"
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={SELECT_STYLES}
            isClearable
            placeholder="All providers"
            options={providerOptions}
            value={providerOptions.find((o) => o.value === filters.provider) || null}
            onChange={(option) => update({ provider: option?.value || "" })}
          />
        </div>
        <div className="gl-filter-select gl-filter-select-sm">
          <Select
            classNamePrefix="gl-select"
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={SELECT_STYLES}
            isClearable
            placeholder="Currency"
            options={currencyOptions}
            value={currencyOptions.find((o) => o.value === filters.currency) || null}
            onChange={(option) => update({ currency: option?.value || "" })}
          />
        </div>
        <div className="gl-filter-actions">
          {hasFilters && (
            <Button color="link" className="gl-reset d-inline-flex align-items-center gap-1" onClick={() => setFilters(EMPTY_FILTERS)}>
              <X size={14} /> Clear
            </Button>
          )}
          {canMutate && (
            <Button color="primary" size="sm" disabled={scan.isPending} onClick={() => scan.mutate()}>
              <RefreshCw size={13} className="me-1" /> {scan.isPending ? "Scanning…" : "Scan now"}
            </Button>
          )}
        </div>
      </div>

      <div className="gl-type-chips">
        {STATUS_CHIPS.map((chip) => (
          <button
            key={chip.key || "all"}
            type="button"
            className={`gl-chip ${filters.status === chip.key ? "active" : ""}`}
            onClick={() => update({ status: chip.key })}
          >
            {chip.label}
            {chip.key === "" && summary?.grand ? <span>{summary.grand.confirmed_count + summary.grand.pending_count + summary.grand.unmatched_count}</span> : null}
          </button>
        ))}
      </div>

      <CurrencySummary summary={summary} />

      {query.isError && (
        <div className="alert alert-danger py-2">{getApiErrorMessage(query.error, "Could not load the reconciliation.")}</div>
      )}

      <div ref={tableRef} style={stickyStyle}>
        <DataTable
          className="gl-table"
          columns={columns}
          customStyles={TABLE_STYLES}
          data={rows}
          keyField="key"
          progressPending={query.isLoading}
          pagination
          paginationPerPage={50}
          paginationRowsPerPageOptions={[25, 50, 100]}
          highlightOnHover
          dense
          responsive={false}
          noDataComponent={<div className="p-4 gl-muted">No income records match the current filters.</div>}
        />
      </div>

      {reviewing && <IncomeReconReviewModal item={reviewing} meta={meta} onClose={() => setReviewing(null)} />}
    </div>
  );
}
